import React from 'react';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import bemBlock from '@gik/core/utils/bemBlock';
import { PropsWithClassName } from '@gik/core/utils/ReactUtils';
import { SectionTitleAndDescription, WishlistCardWithAmazon, WishlistWithAmazonValues } from '@gik/inkind-page';
import { UI } from '@gik/ui';
import noop from '@gik/core/utils/noop';
import { CareCalendarCard, DonationsCard, DonationsValues } from '@gik/create';
import FeatureCheckboxCard from './FeatureCheckboxCard';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { useInkind } from '@gik/api/inkinds/inkind';

export type FeaturesSectionValues = {
  careCalendarEnabled: boolean;
  wishlistValues: WishlistWithAmazonValues;
  donationValues: DonationsValues;
  sendGiftBoxEnabled: boolean;
  enableSuggestedArticles: boolean;
  enableSuggestedProducts: boolean;
};

export interface IFeaturesSectionProps {
  values: FeaturesSectionValues;
  onChange?: (values: FeaturesSectionValues) => void;
  id?: string;
}

export const featuresSectionBlockName = 'features-section';
export const featuresSectionSuggestedItems = 'features-section-suggested-items';

const FeaturesSection: React.FC<PropsWithClassName<IFeaturesSectionProps>> = ({
  values,
  onChange = noop,
  className,
  id,
}): JSX.Element => {
  const bem = bemBlock(featuresSectionBlockName);
  const { t } = useTranslation();

  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkindPage } = useInkind(inkindRouteId);

  function notifyCantDisableAll() {
    UI.notify(t(translationKeys.cantDisableCareCalendarAndWishlist));
  }

  function canDisableCalendar() {
    return !values.wishlistValues.enabled && values.careCalendarEnabled;
  }

  function canDisableWishlist() {
    return !values.careCalendarEnabled && values.wishlistValues.enabled;
  }

  function onToggleCareCalendar(careCalendarEnabled: boolean) {
    if (!canDisableCalendar()) {
      onChange({
        ...values,
        careCalendarEnabled,
      });
    } else {
      notifyCantDisableAll();
    }
  }

  function onToggleWishlist(wishlistValues: WishlistWithAmazonValues) {
    // if Wishlist enabled isn't changed, then Amazon enabled or Amazon url are changed, just let them update
    const isWishlistEnabledChanged = values.wishlistValues.enabled !== wishlistValues.enabled;

    if (!canDisableWishlist() || !isWishlistEnabledChanged) {
      onChange({
        ...values,
        wishlistValues,
      });
    } else {
      notifyCantDisableAll();
    }
  }

  function onToggleDonations(donationValues: DonationsValues) {
    onChange({
      ...values,
      donationValues,
    });
  }

  function onToggleSendGiftBox(sendGiftBoxEnabled: boolean) {
    onChange({
      ...values,
      sendGiftBoxEnabled,
    });
  }

  function onToggleEnableSuggestedArticles(enableSuggestedArticles: boolean) {
    onChange({
      ...values,
      enableSuggestedArticles,
    });
  }

  function onToggleEnableSuggestedProducts(enableSuggestedProducts: boolean) {
    onChange({
      ...values,
      enableSuggestedProducts,
    });
  }

  return (
    <SectionTitleAndDescription
      id={id}
      className={bem(null, null, className)}
      title={t(translationKeys.featuresTitle)}
      description={t(translationKeys.featuresDescription)}
      hasBlueBackground
    >
      <div className={bem('cards')}>
        <CareCalendarCard value={values.careCalendarEnabled} onChange={onToggleCareCalendar} disabled={false} />

        <WishlistCardWithAmazon value={values.wishlistValues} onChange={onToggleWishlist} disabled={false} />

        <DonationsCard value={values.donationValues} onChange={onToggleDonations} disabled={false} />

        <FeatureCheckboxCard
          value={values.sendGiftBoxEnabled}
          onChange={onToggleSendGiftBox}
          title={t(translationKeys.sendGiftBoxTitle)}
          description={t(translationKeys.sendGiftBoxDescription)}
          icon="GiftOutline"
          className={bem('giftbox')}
        />
        <FeatureCheckboxCard
          id={featuresSectionSuggestedItems}
          value={values.enableSuggestedArticles}
          onChange={onToggleEnableSuggestedArticles}
          title={t(translationKeys.suggestedArticlesTitle)}
          description={t(translationKeys.suggestedArticlesDescription)}
          icon="NewspaperOutline"
          className={bem('suggested-articles')}
          locked={!inkindPage?.isPremium}
        />
        <FeatureCheckboxCard
          value={values.enableSuggestedProducts}
          onChange={onToggleEnableSuggestedProducts}
          title={t(translationKeys.suggestedProductsTitle)}
          description={t(translationKeys.suggestedProductsDescription)}
          icon="ViewBoardsOutline"
          className={bem('suggested-products')}
          locked={!inkindPage?.isPremium}
        />
      </div>
    </SectionTitleAndDescription>
  );
};

export default FeaturesSection;
